// Wardrobe APIs

import { apiCall, getDevHeaders } from './client'
import { ApiError } from './errors'

/**
 * Fetches wardrobe items with optional filtering and pagination
 * @param {string} token - Auth token
 * @param {Object} options - Query options
 * @param {string} [options.category] - Filter by category (e.g., 'tops', 'bottoms')
 * @param {string} [options.search] - Search query for item titles
 * @param {string} [options.itemType] - Filter by 'wardrobe' or 'shopping'
 * @param {number} [options.limit=100] - Max items to return
 * @param {number} [options.offset=0] - Pagination offset
 * @returns {Promise<Object>} API response with items array and total count
 */
export async function getWardrobeItems(token, { category, search, itemType, limit = 100, offset = 0 } = {}) {
  const params = new URLSearchParams()
  if (category) params.append('category', category)
  if (search) params.append('search', search)
  if (itemType) params.append('item_type', itemType)
  params.append('limit', limit)
  params.append('offset', offset)

  return apiCall(`/api/wardrobe?${params}`, { token, errorMessage: 'Failed to fetch wardrobe' })
}

export async function getWardrobeItem(token, itemId) {
  return apiCall(`/api/wardrobe/${itemId}`, { token, errorMessage: 'Failed to fetch item' })
}

/**
 * Creates a wardrobe item directly with all metadata provided
 * @param {string} token - Auth token
 * @param {Object} item - Item data
 * @param {string} item.sourceUrl - Original product URL
 * @param {string} item.title - Product title
 * @param {string} item.imageUrl - Product image URL
 * @param {string} [item.site] - Retailer site name
 * @param {string} [item.category] - Clothing category
 * @returns {Promise<Object>} Created wardrobe item
 */
export async function createWardrobeItem(token, { sourceUrl, title, imageUrl, site, category }) {
  return apiCall('/api/wardrobe', {
    token,
    method: 'POST',
    body: { source_url: sourceUrl, title, image_url: imageUrl, site, category },
    errorMessage: 'Failed to add item'
  })
}

/**
 * Imports a wardrobe item from URL or image. Backend uses AI to extract metadata.
 * Returns job object for polling status.
 */
export async function importWardrobeItem(token, { sourceType, url, imageData }) {
  const body = { source_type: sourceType }
  if (sourceType === 'url') body.url = url
  else body.image_data = imageData

  try {
    return await apiCall('/api/wardrobe/import', {
      token,
      method: 'POST',
      body,
      extraHeaders: getDevHeaders(),
      errorMessage: 'Failed to import item'
    })
  } catch (err) {
    if (err.status === 429) {
      throw ApiError.rateLimited("The rack's full — try again in a moment")
    }
    if (err.status === 409 && err.data?.error === 'duplicate') {
      throw ApiError.duplicate(
        err.data.message || 'Item already exists',
        err.data.existing_items
      )
    }
    if (err.data?.error === 'wardrobe_limit_reached') {
      throw ApiError.limitReached('Your wardrobe is full')
    }
    throw err
  }
}

export async function getImportStatus(token, jobId) {
  return apiCall(`/api/wardrobe/import/status/${jobId}`, { token, errorMessage: 'Failed to get import status' })
}

export async function updateWardrobeItem(token, itemId, updates) {
  return apiCall(`/api/wardrobe/${itemId}`, {
    token,
    method: 'PATCH',
    body: updates,
    errorMessage: 'Failed to update item'
  })
}

/**
 * Deletes a wardrobe item (soft delete by default, hard delete if permanent)
 * @param {string} token - Auth token
 * @param {string} itemId - Wardrobe item ID
 * @param {Object} options - Delete options
 * @param {boolean} [options.permanent=false] - If true, permanently deletes item (cannot be restored)
 * @returns {Promise<void>}
 */
export async function deleteWardrobeItem(token, itemId, { permanent = false } = {}) {
  const endpoint = permanent ? `/api/wardrobe/${itemId}?permanent=true` : `/api/wardrobe/${itemId}`
  return apiCall(endpoint, { token, method: 'DELETE', errorMessage: 'Failed to delete item', parseResponse: false })
}

export async function restoreWardrobeItem(token, itemId) {
  return apiCall(`/api/wardrobe/${itemId}/restore`, {
    token,
    method: 'POST',
    errorMessage: 'Failed to restore item',
    parseResponse: false
  })
}

/**
 * Calculates similarity score between two wardrobe items using AI embeddings
 * @param {string} token - Auth token
 * @param {string} itemId1 - First item ID
 * @param {string} itemId2 - Second item ID
 * @returns {Promise<Object>} Similarity response with score between 0-1
 */
export async function getItemSimilarity(token, itemId1, itemId2) {
  return apiCall('/api/wardrobe/similarity', {
    token,
    method: 'POST',
    body: { item_ids: [itemId1, itemId2] },
    errorMessage: 'Failed to calculate similarity'
  })
}
